import React, { useState, useCallback } from "react";
import { ChevronLeft, ChevronRight, Expand, X, Play } from "lucide-react";
import { Dialog, DialogContent } from "./ui/dialog";

export default function PhotoGallery({ images = [], videoUrl, alt = "" }) {
  const [idx, setIdx] = useState(0);
  const [open, setOpen] = useState(false);
  const total = images.length;
  const hasVideo = Boolean(videoUrl);

  const prev = useCallback(() => setIdx((i) => (i - 1 + total) % total), [total]);
  const next = useCallback(() => setIdx((i) => (i + 1) % total), [total]);

  if (!total) {
    return (
      <div className="rounded-2xl bg-slate-100 aspect-[16/10]" aria-hidden />
    );
  }

  return (
    <div data-testid="photo-gallery">
      <div className="relative rounded-2xl overflow-hidden bg-slate-100 aspect-[16/10] group">
        <img
          src={images[idx]}
          alt={alt}
          className="w-full h-full object-cover"
        />
        {total > 1 && (
          <>
            <button
              data-testid="gallery-prev"
              onClick={prev}
              className="absolute left-3 top-1/2 -translate-y-1/2 h-9 w-9 rounded-full bg-white/90 backdrop-blur border border-slate-200 flex items-center justify-center text-slate-700 hover:bg-white transition-colors shadow-sm"
              aria-label="Previous image"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button
              data-testid="gallery-next"
              onClick={next}
              className="absolute right-3 top-1/2 -translate-y-1/2 h-9 w-9 rounded-full bg-white/90 backdrop-blur border border-slate-200 flex items-center justify-center text-slate-700 hover:bg-white transition-colors shadow-sm"
              aria-label="Next image"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </>
        )}
        <button
          data-testid="gallery-expand"
          onClick={() => setOpen(true)}
          className="absolute right-3 top-3 h-9 w-9 rounded-full bg-white/90 backdrop-blur border border-slate-200 flex items-center justify-center text-slate-700 hover:bg-white transition-colors shadow-sm"
          aria-label="Fullscreen"
        >
          <Expand className="w-4 h-4" />
        </button>
        <div className="absolute bottom-3 left-3 text-xs font-semibold px-2 py-1 rounded-md bg-black/60 text-white tabular-nums">
          {idx + 1} / {total}
        </div>
      </div>

      {(total > 1 || hasVideo) && (
        <div className="mt-3 flex items-center gap-2 overflow-x-auto hide-scrollbar">
          {images.map((img, i) => (
            <button
              key={i}
              data-testid={`gallery-thumb-${i}`}
              onClick={() => setIdx(i)}
              className={`shrink-0 w-20 h-14 rounded-lg overflow-hidden border-2 transition-colors ${
                i === idx ? "border-blue-600" : "border-transparent hover:border-slate-200"
              }`}
            >
              <img src={img} alt="" className="w-full h-full object-cover" />
            </button>
          ))}
          {hasVideo && (
            <div
              data-testid="gallery-video-chip"
              className="shrink-0 h-14 px-3 rounded-lg bg-slate-900 text-white text-xs font-semibold inline-flex items-center gap-1.5"
            >
              <Play className="w-3.5 h-3.5" />
              Video
            </div>
          )}
        </div>
      )}

      {/* Lightbox */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-6xl p-0 bg-black border-none">
          <div className="relative">
            <img src={images[idx]} alt="" className="w-full max-h-[85vh] object-contain bg-black" />
            <button
              onClick={() => setOpen(false)}
              className="absolute top-4 right-4 h-9 w-9 rounded-full bg-white/90 text-slate-900 flex items-center justify-center hover:bg-white"
              aria-label="Close"
            >
              <X className="w-4 h-4" />
            </button>
            {total > 1 && (
              <>
                <button
                  onClick={prev}
                  className="absolute left-4 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full bg-white/90 text-slate-900 flex items-center justify-center hover:bg-white"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <button
                  onClick={next}
                  className="absolute right-4 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full bg-white/90 text-slate-900 flex items-center justify-center hover:bg-white"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
